// 🎯 Vandaag-bord — Alles wat vandaag op je bord ligt in 1 oogopslag
// Toont: vlagged mails, snoozed-terug-vandaag, agenda-events vandaag, AI-voorstellen, facturen vervallen
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompanyFunctions } from '@/contexts/CompanyFunctionsContext';
import API from '@/lib/api';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  Flag, Bell, CalendarCheck, Sparkle, Warning,
  EnvelopeSimpleOpen, ArrowRight,
} from '@phosphor-icons/react';

const safeParse = (s) => { try { return parseISO(s); } catch { return new Date(s); } };
const fmtBedrag = (n) => `€ ${Number(n || 0).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SectionCard = ({ icon: Icon, color, title, count, items, emptyMsg, renderItem, onMore }) => (
  <div className="bg-white border-2 rounded-lg p-3 flex flex-col" style={{ borderColor: count > 0 ? color : '#E5E7EB' }}>
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-1.5">
        <Icon size={16} weight="duotone" style={{ color }} />
        <h3 className="text-xs font-bold uppercase text-gray-700">{title}</h3>
      </div>
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: count > 0 ? color + '22' : '#F3F4F6', color: count > 0 ? color : '#9CA3AF' }}>
        {count}
      </span>
    </div>
    <div className="space-y-1 flex-1 overflow-y-auto" style={{ maxHeight: 160 }}>
      {items.length === 0 ? (
        <div className="text-[11px] text-gray-400 italic py-2 text-center">{emptyMsg}</div>
      ) : items.map((it, i) => renderItem(it, i))}
    </div>
    {onMore && (
      <button onClick={onMore} className="text-[10px] text-gray-500 hover:text-gray-800 mt-1 flex items-center gap-0.5 self-end">
        Open <ArrowRight size={10} />
      </button>
    )}
  </div>
);

export default function VandaagBord() {
  const { isVisible } = useCompanyFunctions();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await API.get('/dashboard/vandaag-bord');
      setData(r.data);
    } catch { /* silent fail */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh elke 2 min
  useEffect(() => {
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return null;
  if (!['/mail', '/agenda', '/facturen'].some(isVisible)) return null;

  const totalAttention = (isVisible('/mail') ? (data.totals?.flagged || 0) + (data.totals?.snoozed || 0) +
    (data.totals?.ai_proposals || 0) + (data.totals?.ai_orders || 0) : 0) + (isVisible('/facturen') ? (data.totals?.facturen || 0) : 0);

  return (
    <div className="mb-6" data-testid="vandaag-bord">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-gray-900 flex items-center gap-1.5">
          🎯 Vandaag-bord
          <span className="text-[10px] font-normal text-gray-500">
            {format(new Date(), "EEEE d MMMM yyyy", { locale: nl })}
          </span>
        </h2>
        {totalAttention > 0 && (
          <span className="text-[11px] font-bold text-[#002FA7] bg-blue-50 px-2 py-1 rounded">
            {totalAttention} {totalAttention === 1 ? 'item vraagt aandacht' : 'items vragen aandacht'}
          </span>
        )}
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))' }}>
        {/* 1. Vlagged mails */}
        {isVisible('/mail') && <SectionCard
          icon={Flag} color="#DC2626"
          title="Vlagged mails" count={data.totals?.flagged || 0} items={data.flagged_mails || []}
          emptyMsg="Geen gevlagged mails"
          onMore={() => navigate('/mail')}
          renderItem={(m) => (
            <button
              key={m.id}
              onClick={() => navigate('/mail')}
              className="block w-full text-left flex items-start gap-1.5 px-1.5 py-1 rounded hover:bg-red-50 transition"
              data-testid={`vandaag-flagged-${m.id}`}
            >
              <span className="w-1 self-stretch rounded flex-shrink-0" style={{ background: m.flag_color || '#DC2626' }} />
              <span className="flex-1 min-w-0">
                <span className="block text-[11px] font-medium text-gray-800 truncate">{m.subject || '(geen onderwerp)'}</span>
                <span className="block text-[10px] text-gray-500 truncate">{m.from_name || m.from_email}</span>
              </span>
            </button>
          )}
        />}

        {/* 2. Snoozed terug vandaag */}
        {isVisible('/mail') && <SectionCard
          icon={Bell} color="#F59E0B"
          title="Snoozed terug" count={data.totals?.snoozed || 0} items={data.snoozed_today || []}
          emptyMsg="Niets staat klaar om terug te keren"
          onMore={() => navigate('/mail')}
          renderItem={(m) => (
            <button
              key={m.id}
              onClick={() => navigate('/mail')}
              className="block w-full text-left flex items-start gap-1.5 px-1.5 py-1 rounded hover:bg-amber-50 transition"
              data-testid={`vandaag-snooze-${m.id}`}
            >
              <span className="text-[10px] text-amber-600 font-bold flex-shrink-0 w-9">
                {m.snooze_until ? format(safeParse(m.snooze_until), 'HH:mm') : '--:--'}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[11px] font-medium text-gray-800 truncate">{m.subject || '(geen onderwerp)'}</span>
                <span className="block text-[10px] text-gray-500 truncate">{m.from_name || m.from_email}</span>
              </span>
            </button>
          )}
        />}

        {/* 3. Agenda vandaag */}
        {isVisible('/agenda') && <SectionCard
          icon={CalendarCheck} color="#3B82F6"
          title="Agenda vandaag" count={data.totals?.agenda || 0} items={data.agenda_today || []}
          emptyMsg="Geen afspraken vandaag"
          onMore={() => navigate('/agenda')}
          renderItem={(e) => (
            <button
              key={e.id}
              onClick={() => navigate('/agenda?d=' + (e.start || '').slice(0, 10))}
              className="block w-full text-left flex items-start gap-1.5 px-1.5 py-1 rounded hover:bg-blue-50 transition"
              data-testid={`vandaag-event-${e.id}`}
            >
              <span className="w-1 self-stretch rounded flex-shrink-0" style={{ background: e.kleur || '#3B82F6' }} />
              <span className="flex-1 min-w-0">
                <span className="block text-[10px] text-gray-500">
                  {e.all_day ? '⏰ Hele dag' : (e.start ? format(safeParse(e.start), 'HH:mm') : '')}
                  {!e.all_day && e.eind && ` → ${format(safeParse(e.eind), 'HH:mm')}`}
                </span>
                <span className="block text-[11px] font-medium text-gray-800 truncate">{e.titel}</span>
                {e.locatie && <span className="block text-[9px] text-gray-400 truncate">📍 {e.locatie}</span>}
              </span>
            </button>
          )}
        />}

        {/* 4. AI-voorstellen (afspraken + bestellingen samen) */}
        {isVisible('/mail') && <SectionCard
          icon={Sparkle} color="#8B5CF6"
          title="AI Voorstellen" count={(data.totals?.ai_proposals || 0) + (data.totals?.ai_orders || 0)}
          items={((data.totals?.ai_proposals || 0) + (data.totals?.ai_orders || 0)) > 0 ? [{ events: data.totals?.ai_proposals || 0, orders: data.totals?.ai_orders || 0 }] : []}
          emptyMsg="Geen AI-voorstellen"
          onMore={() => navigate('/mail')}
          renderItem={(it) => (
            <button
              key="proposals"
              onClick={() => navigate('/mail')}
              className="block w-full text-left px-1.5 py-2 rounded hover:bg-violet-50 transition"
              data-testid="vandaag-ai-proposals"
            >
              {it.events > 0 && (
                <div className="text-[11px] font-bold text-violet-700 flex items-center gap-1">
                  <Sparkle size={11} weight="fill" />
                  {it.events} afspraak{it.events === 1 ? '' : 'en'} wacht{it.events === 1 ? '' : 'en'}
                </div>
              )}
              {it.orders > 0 && (
                <div className="text-[11px] font-bold text-emerald-700 flex items-center gap-1 mt-0.5">
                  📦 {it.orders} bestelling{it.orders === 1 ? '' : 'en'} wacht{it.orders === 1 ? '' : 'en'}
                </div>
              )}
              <div className="text-[10px] text-gray-500 mt-0.5">Open Mail Hub → klik Accepteer/Negeer</div>
            </button>
          )}
        />}

        {/* 5. Facturen vervallen */}
        {isVisible('/facturen') && <SectionCard
          icon={Warning} color="#EA580C"
          title="Vervallen vandaag" count={data.totals?.facturen || 0} items={data.facturen_vervallen || []}
          emptyMsg="Geen vervaldagen vandaag"
          onMore={() => navigate('/facturen')}
          renderItem={(f) => (
            <button
              key={f.id}
              onClick={() => navigate(`/factuur/${f.id}`)}
              className="block w-full text-left flex items-start gap-1.5 px-1.5 py-1 rounded hover:bg-orange-50 transition"
              data-testid={`vandaag-factuur-${f.id}`}
            >
              <EnvelopeSimpleOpen size={12} className="text-orange-600 flex-shrink-0 mt-0.5" />
              <span className="flex-1 min-w-0">
                <span className="block text-[11px] font-medium text-gray-800 truncate">
                  {f.factuurnummer || '?'} • {fmtBedrag(f.te_betalen || f.totaal || 0)}
                </span>
                <span className="block text-[10px] text-gray-500 truncate">{f.klant_naam || '?'}</span>
              </span>
            </button>
          )}
        />}
      </div>
    </div>
  );
}
